import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight, Bot, Brain, Check, LayoutGrid, PlugZap, Sparkles, X, Zap } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { SlackPanel } from '../components/SlackIntegrationGraph';
import { CodeTerminal, SurfaceTab, TabIcon, type SurfaceKey } from './CompanyBrain';
import { MoreThanAnswers } from './GigaForChatGPT2';
import { ConnectedAppsPillar } from '../components/alt/ConnectedAppsPillar';
import { PlatformPillar } from '../components/alt/PlatformPillar';
import { CompanyBrainPillar } from '../components/alt/CompanyBrainPillar';

// Order carries the argument: connect the tools, build with them, take that
// intelligence to where people work, and let it compound.
const SECTIONS = [{
  id: 'connected-apps',
  label: 'Connected Apps',
  icon: PlugZap
}, {
  id: 'platform',
  label: 'Platform',
  icon: LayoutGrid
}, {
  id: 'co-worker',
  label: 'AI Co Worker',
  icon: Bot
}, {
  id: 'company-brain',
  label: 'Company Brain',
  icon: Brain
}] as const;
type SectionId = (typeof SECTIONS)[number]['id'];
const HEADLINE_ROTATION_MS = 4200;
const HEADLINE_ROTATION_OFFSET_MS = 2100;
export function Alt() {
  const [activeSection, setActiveSection] = useState<SectionId>('connected-apps');
  const [activeSurface, setActiveSurface] = useState<SurfaceKey>('slack');
  const [headlineIndex, setHeadlineIndex] = useState(0);
  const [audienceIndex, setAudienceIndex] = useState(0);
  const navRef = useRef<HTMLDivElement | null>(null);
  const lastScrollYRef = useRef(0);
  const headlinePhrases = ['Go Faster', 'Stay in Control'];
  const audiencePhrases = ['companies', 'founders', 'operators', 'small businesses'];
  useEffect(() => {
    const interval = window.setInterval(() => {
      setHeadlineIndex(current => (current + 1) % headlinePhrases.length);
    }, HEADLINE_ROTATION_MS);
    return () => window.clearInterval(interval);
  }, [headlinePhrases.length]);
  useEffect(() => {
    let interval: number | undefined;
    const timeout = window.setTimeout(() => {
      setAudienceIndex(current => (current + 1) % audiencePhrases.length);
      interval = window.setInterval(() => {
        setAudienceIndex(current => (current + 1) % audiencePhrases.length);
      }, HEADLINE_ROTATION_MS);
    }, HEADLINE_ROTATION_OFFSET_MS);
    return () => {
      window.clearTimeout(timeout);
      if (interval) window.clearInterval(interval);
    };
  }, [audiencePhrases.length]);
  useEffect(() => {
    const sections = SECTIONS.map(section => document.getElementById(section.id)).filter((section): section is HTMLElement => Boolean(section));
    if (sections.length === 0) return;
    const observer = new IntersectionObserver(entries => {
      const scrollingDown = window.scrollY >= lastScrollYRef.current;
      lastScrollYRef.current = window.scrollY;
      const entering = entries.filter(entry => entry.isIntersecting).sort((a, b) => SECTIONS.findIndex(section => section.id === a.target.id) - SECTIONS.findIndex(section => section.id === b.target.id));
      const next = scrollingDown ? entering[entering.length - 1] : entering[0];
      if (next?.target.id) {
        setActiveSection(next.target.id as SectionId);
      }
    }, {
      rootMargin: '-32% 0px -67% 0px',
      threshold: 0
    });
    sections.forEach(section => observer.observe(section));
    return () => observer.disconnect();
  }, []);
  const scrollToSection = (sectionId: SectionId) => {
    const el = document.getElementById(sectionId);
    if (!el) return;
    const navHeight = navRef.current?.offsetHeight ?? 0;
    const headerOffset = 76;
    const top = el.getBoundingClientRect().top + window.scrollY - navHeight - headerOffset - 22;
    window.scrollTo({
      top,
      behavior: 'smooth'
    });
  };
  return <div className="min-h-screen pt-24 text-[#2B211A]" style={{
    backgroundColor: '#FAF8F4',
    backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='8' height='8' viewBox='0 0 8 8' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='2' height='2' fill='%232B211A' fill-opacity='0.10'/%3E%3C/svg%3E\")",
    backgroundSize: '8px 8px'
  }} data-id="element-5882" data-mp="hcmhs2w2vn9e">
      <section className="pb-10 pt-12 md:pb-14 md:pt-20" data-id="element-5883" data-mp="39jwo51ep5d23">
        <div className="mx-auto max-w-[1280px] px-6 md:px-10" data-id="element-5884" data-mp="39ketcnjc71m5">
          <div className="mx-auto max-w-5xl text-center" data-id="element-5885" data-mp="3dk714y0wp3qg">
            <h1 className="font-title text-5xl font-bold leading-[0.95] tracking-tight text-[#2B211A] md:text-7xl" data-id="element-5886" data-mp="2guy2hubd7ss1">
              The AI Platform for{' '}
              <span className="inline-flex overflow-hidden align-baseline" data-id="element-5887" data-mp="2lukek0fgj2z9">
                <AnimatePresence mode="wait" initial={false} data-id="element-5888" data-mp="2pve5l2o74xwd">
                  <motion.span key={audiencePhrases[audienceIndex]} initial={{
                  y: '100%',
                  opacity: 0,
                  rotateX: -60
                }} animate={{
                  y: 0,
                  opacity: 1,
                  rotateX: 0
                }} exit={{
                  y: '-100%',
                  opacity: 0,
                  rotateX: 60
                }} transition={{
                  duration: 0.42,
                  ease: [0.22, 1, 0.36, 1]
                }} className="block" data-id="element-5889" data-mp="2umcjxwpmjlwz">
                    {audiencePhrases[audienceIndex]}
                  </motion.span>
                </AnimatePresence>
              </span>
              <br data-id="element-5890" data-mp="1govqfd5wskqc" />
              that want to{' '}
              <span className="relative inline-flex overflow-hidden align-baseline" data-id="element-5891" data-mp="1boq3296o9ke2">
                <span className="absolute inset-x-0 bottom-[-4px] h-[9px] bg-[#EA580C]" aria-hidden="true" data-id="element-5892" data-mp="16wg8gfum3w5g" />
                <AnimatePresence mode="wait" initial={false} data-id="element-5893" data-mp="11x3gjgovvyw0">
                  <motion.span key={headlinePhrases[headlineIndex]} initial={{
                  y: '100%',
                  opacity: 0,
                  rotateX: -60
                }} animate={{
                  y: 0,
                  opacity: 1,
                  rotateX: 0
                }} exit={{
                  y: '-100%',
                  opacity: 0,
                  rotateX: 60
                }} transition={{
                  duration: 0.42,
                  ease: [0.22, 1, 0.36, 1]
                }} className="relative z-10 block" data-id="element-5894" data-mp="xw9wiyekb48e">
                    {headlinePhrases[headlineIndex]}
                  </motion.span>
                </AnimatePresence>
              </span>
            </h1>

            {/* The whole page in one line, so the four tabs below read as a
             sequence rather than a menu of features. */}
            <p className="mx-auto mt-8 max-w-[64ch] text-lg leading-relaxed text-[#6B5D52] md:text-xl" data-id="element-5895" data-mp="11yr9w2yfaofw">
              Connect the tools your company runs on, build the way your team
              works with AI, put that AI everywhere people already work, and let
              what it learns compound.
            </p>
          </div>
        </div>
      </section>

      <div ref={navRef} className="sticky top-[72px] z-40" data-id="element-5896" data-mp="1kqacw9sg62hx">
        <nav className="mx-auto grid max-w-[1280px] grid-cols-2 gap-2 px-3 py-4 sm:grid-cols-4 sm:gap-3 sm:px-6 md:flex md:justify-center md:px-10" aria-label="Section navigation" data-id="element-5897" data-mp="1krv8m2qhzrhd">
          {SECTIONS.map(section => {
          const isActive = activeSection === section.id;
          const Icon = section.icon;
          return <button key={section.id} type="button" onClick={() => scrollToSection(section.id)} className={`inline-flex min-w-0 items-center justify-center gap-1.5 rounded-full border px-2 py-2.5 text-[clamp(0.72rem,2.7vw,1rem)] font-semibold shadow-sm transition-all sm:gap-2 sm:px-4 md:shrink-0 md:px-5 md:text-lg ${isActive ? 'border-[#EA580C] bg-[#EA580C] text-white shadow-md hover:bg-[#C2410C]' : 'border-neutral-200 bg-white text-neutral-600 hover:border-[#EA580C]/30 hover:text-[#2B211A]'}`} aria-current={isActive ? 'true' : undefined} data-id="element-5898" data-mp="3a7pd1f73yuki">
                <Icon size={19} className={`shrink-0 ${isActive ? 'text-white' : 'text-neutral-400'}`} data-id="element-5899" data-mp="arivlfi1actc" />
                <span className="min-w-0 leading-tight" data-id="element-5900" data-mp="6q775dawbxmq">{section.label}</span>
              </button>;
        })}
        </nav>
      </div>

      <main data-id="element-5901" data-mp="207uzrykimc95">
        <section id="connected-apps" className="scroll-mt-40 border-b border-neutral-200/70 bg-[#FAF8F4] py-20 md:py-28" data-id="element-5902" data-mp="258shka1m54a4">
          <div className="mx-auto max-w-[1280px] px-6 md:px-10" data-id="element-5903" data-mp="2a7c7eljbv07r">
            <ConnectedAppsPillar data-id="element-5904" data-mp="2a60zqamrh8bd" />
            <div className="mt-20 md:mt-28" data-id="element-5905" data-mp="2eyu4gyi8cgng">
              <h3 className="mb-8 font-title text-3xl font-bold leading-tight tracking-tight text-[#2B211A] md:text-4xl" data-id="element-5906" data-mp="2ezwyhfaumz7l">
                Why connections belong in Giga, not in a chat app
              </h3>
              <ConnectedAppsTable data-id="element-5907" data-mp="2o1d5dd2ggj72" />
            </div>
          </div>
        </section>

        <section id="platform" className="scroll-mt-40 border-b border-neutral-200/70 bg-white py-20 md:py-28" data-id="element-5908" data-mp="2sz4vq2ngbx95">
          <div className="mx-auto max-w-[1280px] px-6 md:px-10" data-id="element-5909" data-mp="2xvdgbvkf4inp">
            <PlatformPillar data-id="element-5910" data-mp="ylngr41448pq" />
          </div>
        </section>

        <section id="co-worker" className="scroll-mt-40 border-b border-neutral-200/70 bg-[#FAF8F4] py-20 md:py-28" data-id="element-5911" data-mp="13j5lei83k1rx">
          <div className="mx-auto max-w-[1280px] px-6 md:px-10" data-id="element-5912" data-mp="3gtymn794d96i">
            <div className="mx-auto mb-12 max-w-3xl text-center md:mb-16" data-id="element-5913" data-mp="3c3037ysu3uz1">
              <p className="mb-4 text-[12px] font-bold uppercase tracking-[0.14em] text-[#EA580C]" data-id="element-5914" data-mp="3c0vmgg9u71hc">
                Everywhere your team works
              </p>
              <h2 className="font-title text-4xl font-bold leading-[1.05] tracking-tight text-[#2B211A] md:text-[56px]" data-id="element-5915" data-mp="333cwqyl8zyz4">
                Your AI doesn't have to
                <br data-id="element-5916" data-mp="3vqzb91w0ko0r" />
                live in another app.
              </h2>
              <p className="mx-auto mt-5 max-w-[54ch] text-lg leading-relaxed text-[#6B5D52]" data-id="element-5917" data-mp="3vouxztc86cnt">
                Everything your company builds in Giga can show up as a co
                worker in Slack, ChatGPT, and Claude Code — with Teams and
                iMessage next. Same connections, same brain, no new tab.
              </p>
            </div>

            <div className="grid items-center gap-10 md:grid-cols-[1.2fr_0.8fr]" data-id="element-5918" data-mp="2acl60gmyc2j6">
              <SurfacePreview activeSurface={activeSurface} setActiveSurface={setActiveSurface} data-id="element-5919" data-mp="20u5kdwqtaylp" />

              <div data-id="element-5920" data-mp="1vw3yz55wyn72">
                <SurfaceTabsControl activeSurface={activeSurface} setActiveSurface={setActiveSurface} className="mb-8 hidden md:flex" tabClassName="!px-4 !py-2.5 !text-base" data-id="element-5921" data-mp="1vwn6drxxtt6u" />

                <div className="space-y-4 font-title text-xl text-[#2B211A] md:text-2xl" style={{
                fontWeight: 500
              }} data-id="element-5922" data-mp="25t8bed9yj4ao">
                  <IconBullet icon={PlugZap} label="Reads and writes in your connected tools" data-id="element-5923" data-mp="2jjbuivdjihvw" />
                  <IconBullet icon={Zap} label="Real work: audits, analysis, reports, presentations" data-id="element-5924" data-mp="2elttdr5rl6yg" />
                  <IconBullet icon={Sparkles} label="Autonomous and proactive" data-id="element-5925" data-mp="2tikuq5z00sqp" />
                </div>
                <p className="mt-6 max-w-[46ch] text-[15px] leading-relaxed text-[#6B5D52]" data-id="element-5926" data-mp="2ol19gm3vvfp9">
                  The co worker is one interface into the platform — not a
                  separate product with its own setup to maintain.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section id="company-brain" className="scroll-mt-40 bg-white py-20 md:py-28" data-id="element-5927" data-mp="3asorj6whlx2s">
          <div className="mx-auto max-w-[1280px] px-6 md:px-10" data-id="element-5928" data-mp="3auhp7wp4h6yn">
            <CompanyBrainPillar data-id="element-5929" data-mp="2w2z9m006deft" />
          </div>
        </section>

        <section className="bg-[#FAF8F4] py-20 md:py-28" data-id="element-5930" data-mp="312bw2jn4wjhv">
          <div className="mx-auto max-w-[1280px] px-6 md:px-10" data-id="element-5931" data-mp="310sgkv9aych5">
            <AccelerationControlSection data-id="element-5932" data-mp="2n2aybd261s6m" />
            <ClosingCTA data-id="element-5933" data-mp="2n5gn0r6vus1t" />
          </div>
        </section>
      </main>
    </div>;
}
function SurfacePreview({
  activeSurface,
  setActiveSurface
}: {
  activeSurface: SurfaceKey;
  setActiveSurface: (surface: SurfaceKey) => void;
}) {
  const mobileTabs = <SurfaceTabsControl activeSurface={activeSurface} setActiveSurface={setActiveSurface} className="relative z-10 flex md:hidden" tabClassName="!flex-1 !justify-center !px-2 !py-2 !text-xs" quiet data-id="element-5934" data-mp="2i52gxeouds6f" />;
  if (activeSurface === 'chatgpt') {
    return <div className="relative mx-auto w-full max-w-[760px] rounded-[28px] bg-[#EA580C]/10 p-2 pt-1 md:bg-transparent md:p-0" data-id="element-5935" data-mp="gaa6oej05pm5">
        <div className="absolute -inset-3 hidden rounded-[28px] bg-[#EA580C]/10 md:block" aria-hidden="true" data-id="element-5936" data-mp="9vuusytj4f5m" />
        {mobileTabs}
        <div className="relative rounded-[22px] rounded-tr-none bg-white p-3 text-left shadow-[0_28px_80px_rgba(43,33,26,0.13)] md:rounded-[24px]" data-id="element-5937" data-mp="4y2srbz67cnp">
          <MoreThanAnswers embedded data-id="element-5938" data-mp="xlyvaxbf7yse" />
        </div>
      </div>;
  }
  if (activeSurface === 'code') {
    return <div className="relative mx-auto w-full max-w-[760px] rounded-[28px] bg-[#EA580C]/10 p-2 pt-1 md:bg-transparent md:p-0" data-id="element-5939" data-mp="sn5iz87vedtt">
        {mobileTabs}
        <CodeTerminal data-id="element-5940" data-mp="nukqca0pa6rv" />
      </div>;
  }
  return <div className="relative mx-auto w-full max-w-[760px] rounded-[28px] bg-[#EA580C]/10 p-2 pt-1 md:bg-transparent md:p-0" data-id="element-5941" data-mp="ix2eobvatdg6">
      <div className="absolute -inset-3 hidden rounded-[28px] bg-[#EA580C]/10 md:block" aria-hidden="true" data-id="element-5942" data-mp="1gcq1xr40xb33" />
      {mobileTabs}
      <div className="relative text-left [&>div]:rounded-tl-none md:[&>div]:rounded-tl-2xl" data-id="element-5943" data-mp="1bk6pwykzio0e">
        <SlackPanel data-id="element-5944" data-mp="1jvp4k5gwyfc8" />
      </div>
    </div>;
}
function SurfaceTabsControl({
  activeSurface,
  setActiveSurface,
  className = '',
  tabClassName = '',
  quiet = false
}: {
  activeSurface: SurfaceKey;
  setActiveSurface: (surface: SurfaceKey) => void;
  className?: string;
  tabClassName?: string;
  quiet?: boolean;
}) {
  if (quiet) {
    const tabs = [{
      surface: 'slack' as const,
      label: 'Slack',
      icon: <TabIcon src="https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/slack.svg" fallback="S" data-id="element-5945" data-mp="22szw13wb04h4" />
    }, {
      surface: 'code' as const,
      label: 'Claude',
      icon: <TabIcon slug="claude" fallback="C" data-id="element-5946" data-mp="2bmuy2324pesj" />,
      ariaLabel: 'Claude'
    }, {
      surface: 'chatgpt' as const,
      label: 'ChatGPT',
      icon: <TabIcon src="https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/openai.svg" fallback="AI" data-id="element-5947" data-mp="2ljpwyn2bdh7c" />
    }];
    return <div className={`w-full items-center gap-0 ${className}`} role="tablist" aria-label="Chat surface" data-id="element-5948" data-mp="2qiaycptay6xd">
        {tabs.map(tab => {
        const active = activeSurface === tab.surface;
        return <button key={tab.surface} type="button" role="tab" aria-selected={active} aria-label={tab.ariaLabel} onClick={() => setActiveSurface(tab.surface)} className={`inline-flex items-center gap-1.5 rounded-t-[16px] border border-transparent px-2 py-2 text-xs text-[#2B211A] transition-colors ${active ? 'bg-white font-bold shadow-sm' : 'font-semibold text-[#6B5D52]'} ${tabClassName}`} data-id="element-5949" data-mp="3ibe7de99o2rn">
              {tab.icon}
              {tab.label}
            </button>;
      })}
      </div>;
  }
  return <div className={`w-full items-center gap-2.5 md:gap-3 ${className}`} role="tablist" aria-label="Chat surface" data-id="element-5950" data-mp="3dc1kwumb4xpl">
      <SurfaceTab surface="slack" activeSurface={activeSurface} setActiveSurface={setActiveSurface} className={tabClassName} data-id="element-5951" data-mp="399wvk82w6n8r">
        <TabIcon src="https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/slack.svg" fallback="S" data-id="element-5952" data-mp="skhnh9xt1vvz" />
        Slack
      </SurfaceTab>
      <SurfaceTab surface="code" activeSurface={activeSurface} setActiveSurface={setActiveSurface} ariaLabel="Claude" className={tabClassName} data-id="element-5953" data-mp="omjxi4w7n5rj">
        <TabIcon slug="claude" fallback="C" data-id="element-5954" data-mp="11332xwmm2ede" />
        Claude
      </SurfaceTab>
      <SurfaceTab surface="chatgpt" activeSurface={activeSurface} setActiveSurface={setActiveSurface} className={tabClassName} data-id="element-5955" data-mp="m5snydareqcx">
        <TabIcon src="https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/openai.svg" fallback="AI" data-id="element-5956" data-mp="r5osqera6ltj" />
        ChatGPT
      </SurfaceTab>
    </div>;
}
const ACCELERATION_CONTROL_GROUPS = [{
  heading: 'Go Faster',
  items: [{
    title: 'Offload busywork and associate work',
    copy: 'Hand Giga the repetitive research, formatting, follow-up, and first-draft work so your team can spend more time on judgment calls.'
  }, {
    title: 'Kill Meetings',
    copy: 'Turn scattered conversations into crisp updates, decisions, and next steps without adding another recurring sync to the calendar.'
  }, {
    title: 'Stop waiting for numbers',
    copy: 'Pull live metrics from the systems you already use and get answers, reports, and analysis without waiting on manual exports.'
  }]
}, {
  heading: 'Stay In Control',
  items: [{
    title: 'Own your own data',
    copy: 'Export your data, keep your history portable, and switch systems whenever you need without losing the work your team has built.'
  }, {
    title: 'Get Real Time Visibility',
    copy: 'See what is connected, what is being used, and where sensitive work is happening across your team as it happens.'
  }, {
    title: 'Built for teams',
    copy: 'Manage shared access, team workflows, and company context from one place instead of stitching together personal AI accounts.'
  }]
}] as const;
function AccelerationControlSection() {
  return <div data-id="element-5957" data-mp="1fr36lze801bf">
      {ACCELERATION_CONTROL_GROUPS.map((group, groupIndex) => <section key={group.heading} className={groupIndex > 0 ? 'mt-16 md:mt-20' : undefined} data-id="element-5958" data-mp="1aqoxee0vulbt" data-mpi={groupIndex}>
          <h2 className="font-title text-4xl font-bold leading-tight text-[#2B211A] md:text-6xl" data-id="element-5959" data-mp="15qsvqlfnr5zf">
            {group.heading}
          </h2>
          <div className="mt-8 border-y border-neutral-200" data-id="element-5960" data-mp="3heftl66dyb1k">
            {group.items.map((item, index) => <motion.article key={item.title} initial={{
          opacity: 0,
          y: 18
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.42,
          delay: index * 0.03,
          ease: [0.22, 1, 0.36, 1]
        }} className="grid gap-4 border-b border-neutral-200 py-8 last:border-b-0 md:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] md:items-center md:gap-8 md:py-10" data-id="element-5961" data-mp="3mc7nu6ui698x" data-mpi={index}>
                <h3 className="font-title text-2xl font-bold leading-tight text-[#2B211A] md:text-3xl" data-id="element-5962" data-mp="37mrvimfbpvr4">{item.title}</h3>
                <p className="max-w-xl text-base leading-7 text-[#6B5D52] md:text-lg md:leading-8" data-id="element-5963" data-mp="2fxostb334tgl">{item.copy}</p>
              </motion.article>)}
          </div>
        </section>)}
    </div>;
}
function ClosingCTA() {
  return <div className="mt-20 overflow-hidden rounded-[28px] bg-[#2B211A] px-8 py-14 text-center md:mt-28 md:px-16 md:py-20" data-id="element-5964" data-mp="1dbg1jlhj16mu">
      <h2 className="mx-auto max-w-[24ch] font-title text-4xl font-bold leading-[1.05] tracking-tight text-white md:text-[52px]" data-id="element-5965" data-mp="18dy1kklhm1qz">
        One platform to run your company's AI on.
      </h2>
      <p className="mx-auto mt-5 max-w-[52ch] text-lg leading-relaxed text-white/65" data-id="element-5966" data-mp="1iasxcvqdlo1k">
        Connect your tools, build your workflows, put a co worker where your
        team already is, and let the brain get sharper every week.
      </p>
      <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row" data-id="element-5967" data-mp="1rbh5j9t38u6j">
        <Link to="/signup" className="inline-flex items-center gap-2 rounded-full bg-[#EA580C] px-7 py-3.5 text-base font-bold text-white transition-colors hover:bg-[#C2410C]" data-id="element-5968" data-mp="2603p5npr083k">
          Get started for free
          <ArrowRight size={17} data-id="element-5969" data-mp="2f1l95h6a0txj" />
        </Link>
        <Link to="/demo/book" className="inline-flex items-center rounded-full border border-white/25 px-7 py-3.5 text-base font-bold text-white transition-colors hover:border-white/60" data-id="element-5970" data-mp="2b09lviavkksi">
          Book a demo
        </Link>
      </div>
      <p className="mt-6 text-[13.5px] text-white/45" data-id="element-5971" data-mp="2ymlelinpcxgv">
        $100 in credits included · no card needed
      </p>
    </div>;
}
type ConnectedAppsValue = boolean | 'Enterprise only' | 'Coming soon' | null;
const CONNECTED_APPS_ROWS: Array<{
  feature: string;
  description: string;
  chatgpt: ConnectedAppsValue;
  giga: ConnectedAppsValue;
}> = [{
  feature: 'Supports MCP connections',
  description: 'Connect through existing MCP servers from supported providers.',
  chatgpt: true,
  giga: true
}, {
  feature: 'Supports API connections',
  description: 'Stop having your team paste API tokens into conversations in plain text.',
  chatgpt: false,
  giga: true
}, {
  feature: 'Supports SQL connections',
  description: 'See which databases your team connects to, review their permissions, and flag anything that looks dangerous.',
  chatgpt: false,
  giga: true
}, {
  feature: 'Supports email/password connections',
  description: "Use Giga's cloud browser for tools without an API or MCP, so your agent can drive the browser and finish the work.",
  chatgpt: false,
  giga: true
}, {
  feature: 'Supports unlisted tools',
  description: 'Use the on-demand integration wizard to add tools that are not yet in our directory.',
  chatgpt: false,
  giga: true
}, {
  feature: 'Supports multiple accounts',
  description: 'Connect personal, team, client, and environment accounts without repeatedly signing in and out.',
  chatgpt: false,
  giga: true
}, {
  feature: "Gives admins full visibility on the team's connections",
  description: 'Give admins one view of every connection, its owner, permissions, and recent use.',
  chatgpt: false,
  giga: true
}, {
  feature: 'See what tools employees have connected',
  description: 'Understand which tools employees have authorized and where company data can flow.',
  chatgpt: 'Enterprise only',
  giga: true
}, {
  feature: 'Bring your own OAuth providers',
  description: 'Use your own OAuth app and consent screen when you need complete control over authorization.',
  chatgpt: null,
  giga: true
}, {
  feature: 'Audit activity',
  description: 'Review how connections are used, by whom, and when, with detailed activity history.',
  chatgpt: null,
  giga: 'Coming soon'
}];
function ConnectedAppsTable() {
  return <div className="overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-[0_30px_80px_-48px_rgba(43,33,26,0.4)]" data-id="element-5972" data-mp="83thzhg12ent">
      <div className="overflow-x-auto" data-id="element-5973" data-mp="3732owc1rn3g">
        <table className="w-full min-w-[760px] border-collapse text-left" data-id="element-5974" data-mp="h59p9gbjq0nt">
          <thead data-id="element-5975" data-mp="h36tefnppdb6">
            <tr className="border-b border-neutral-200" data-id="element-5976" data-mp="c66nq5uryckl">
              <th scope="col" className="w-[54%] px-6 py-6 md:px-8" data-id="element-5977" data-mp="c4n6abxdtvh8">
                <span className="sr-only" data-id="element-5978" data-mp="1sjnb6fmqo617">Feature</span>
              </th>
              <th scope="col" className="w-[19%] px-5 py-6 font-title text-xl font-bold text-[#2B211A] md:text-2xl" data-id="element-5979" data-mp="1nllu208vigsd">
                ChatGPT
              </th>
              <th scope="col" className="w-[27%] bg-[#FFF8F3] px-5 py-6 font-title text-xl font-bold text-[#2B211A] md:text-2xl" data-id="element-5980" data-mp="l7nxxrvmjdzs">
                Giga
              </th>
            </tr>
          </thead>
          <tbody data-id="element-5981" data-mp="u9f0woys56py">
            {CONNECTED_APPS_ROWS.map(row => <tr key={row.feature} className="border-b border-neutral-100 last:border-b-0" data-id="element-5982" data-mp="z88gqo1jh9ta">
                <th scope="row" className="px-6 py-5 text-base font-medium leading-6 text-[#2B211A] md:px-8 md:text-lg" data-id="element-5983" data-mp="2hzi760cg450">
                  <span className="block" data-id="element-5984" data-mp="6k2ufwuu7crc">{row.feature}</span>
                  <span className="mt-1.5 block max-w-2xl text-sm font-normal leading-5 text-[#8A7B70]" data-id="element-5985" data-mp="bi4bkc8pd206">
                    {row.description}
                  </span>
                </th>
                <td className="px-5 py-5 text-[#2B211A]" data-id="element-5986" data-mp="gap8hmmwnv7x">
                  <ComparisonValue value={row.chatgpt} data-id="element-5987" data-mp="3ey352ymdq81g" />
                </td>
                <td className="bg-[#FFF8F3] px-5 py-5 text-[#2B211A]" data-id="element-5988" data-mp="3jx5xnjl6alsj">
                  <ComparisonValue value={row.giga} highlight data-id="element-5989" data-mp="3ceexdnkfgvst" />
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
}
function ComparisonValue({
  value,
  highlight = false
}: {
  value: ConnectedAppsValue;
  highlight?: boolean;
}) {
  if (value === true) {
    return <span className={`inline-flex items-center ${highlight ? 'text-[#EA580C]' : 'text-[#2B211A]'}`} aria-label="Supported" data-id="element-5990" data-mp="2yexaxeultumm">
        <Check size={25} strokeWidth={2.6} aria-hidden="true" data-id="element-5991" data-mp="2tgm1v6c4vo5g" />
      </span>;
  }
  if (value === false) {
    return <span className="inline-flex items-center text-neutral-300" aria-label="Not supported" data-id="element-5992" data-mp="2oo2ejhnn9eng">
        <X size={25} strokeWidth={2.4} aria-hidden="true" data-id="element-5993" data-mp="2jqj0oiuuz3w1" />
      </span>;
  }
  if (value === null) {
    return <span aria-label="Not available" className="text-neutral-300" data-id="element-5994" data-mp="2erzfwls03y59">—</span>;
  }
  return <span className={`text-sm font-semibold ${highlight ? 'text-[#EA580C]' : 'text-[#6B5D52]'}`} data-id="element-5995" data-mp="2ao1bpwntjx5c">{value}</span>;
}
function IconBullet({
  icon: Icon,
  label
}: {
  icon: React.ElementType;
  label: string;
}) {
  return <p className="flex items-center gap-3" data-id="element-5996" data-mp="9nd9xs74to8n">
      <span className="flex size-6 shrink-0 items-center justify-center text-[#EA580C]" data-id="element-5997" data-mp="dof8e6t85w9k">
        <Icon size={22} strokeWidth={2.4} data-id="element-5998" data-mp="dq9o96pn2y6s" />
      </span>
      {label}
    </p>;
}